import { comboboxClassNames, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import {
  HEADER_HEIGHT,
  HEADER_SMALL_BREAKPOINT,
  MOBILE_NAV_MAX_WIDTH_BREAKPOINT,
} from '../../../app/common/constants/SiteConstants';

/**
 * Every responsive rule for the site header lives in this file and is expressed as a CSS
 * media query. No header component may read `window.innerWidth`, register a `resize`
 * listener, or call `matchMedia` — layout that changes with viewport width is the
 * stylesheet's job, not React's.
 *
 * Breakpoint: `MOBILE_NAV_MAX_WIDTH_BREAKPOINT` (max-width 1023px), shared with
 * `Navigation.tsx` so the header and the left nav switch layouts together.
 */

/** Non-production environments get a red header so the environment is unmistakable. */
const NON_PROD_BACKGROUND = '#d40004';

/**
 * The bar colour is published as a custom property so the buttons can invert to it on
 * hover without restating it. `rootNonProd` only has to redefine this one value and both
 * the bar and every inverted foreground follow — they cannot drift apart.
 */
const HEADER_BACKGROUND_VAR = '--stat-header-background';
const HEADER_BACKGROUND = `var(${HEADER_BACKGROUND_VAR})`;

/**
 * The collapsed search matches the icon buttons exactly — same box size, same gap — so all
 * four icons end up evenly spaced without anything being positioned by hand.
 */
const ICON_BUTTON_SIZE = '32px';
const ICON_GAP = tokens.spacingHorizontalXXS;
/** Resting width of the centred search field on the full layout. */
const SEARCH_WIDTH = '420px';

const useStatHeaderStyles = makeStyles({
  root: {
    [HEADER_BACKGROUND_VAR]: tokens.colorBrandBackground,
    position: 'relative',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    height: `${HEADER_HEIGHT}px`,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalS,
    backgroundColor: HEADER_BACKGROUND,
    color: tokens.colorNeutralForegroundOnBrand,
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 2,
      // Matches the gap between the action buttons, so the collapsed search is spaced
      // identically to them. The title absorbs the slack because it grows to fill.
      columnGap: ICON_GAP,
    },
  },
  rootNonProd: {
    [HEADER_BACKGROUND_VAR]: NON_PROD_BACKGROUND,
  },

  /**
   * The title and the action group are both `flex: 1 0 0` with a `max-content` floor, so
   * they claim equal space either side of the search field and the field ends up centred in
   * the header bar. Neither can be squeezed below its own content, so the brand and the
   * buttons stay intact; the search absorbs any shortfall instead.
   */
  titleLink: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '24px',
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    minWidth: 'max-content',
    color: tokens.colorNeutralForegroundOnBrand,
    textDecorationLine: 'none',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    // The colour is restated for every interactive state on purpose. A global `a:hover`
    // rule from the Fluent/Coherence stylesheets has specificity (0,1,1) and would beat a
    // plain atomic class (0,1,0), turning the brand link link-blue on hover; a Griffel
    // pseudo-class rule is (0,2,0) and wins.
    ':hover': {
      color: tokens.colorNeutralForegroundOnBrand,
      textDecorationLine: 'underline',
    },
    ':hover:active': {
      color: tokens.colorNeutralForegroundOnBrand,
      textDecorationLine: 'underline',
    },
    ':visited': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':focus': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorNeutralForegroundOnBrand,
      outlineOffset: '2px',
    },
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      fontSize: tokens.fontSizeBase300,
      // Yields the row to the search when it expands. `min-width: 0` is what actually
      // permits the shrink; the `max-content` floor from the full layout would block it.
      flexShrink: 1,
      minWidth: 0,
      overflowX: 'hidden',
      overflowY: 'hidden',
    },
  },
  /**
   * `text-overflow` has no effect on a flex container, so the truncation lives on the inner
   * text run rather than on the anchor itself.
   */
  titleText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    overflowY: 'hidden',
    textOverflow: 'ellipsis',
  },
  /**
   * The " - <environment>" suffix is dropped on the compact layout to leave room for the
   * search field. The link keeps the full text in its `aria-label`, so the accessible name
   * is identical at every width.
   */
  titleEnvironment: {
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      display: 'none',
    },
  },

  /**
   * Compact layout: the search shrinks to a single icon that sits in the flex row alongside
   * the action buttons — same 32px box, same gap — so all four icons are evenly spaced by
   * the layout itself rather than by hand-tuned offsets. `:focus-within` — which fires for
   * pointer, touch and keyboard alike, because Fluent's Combobox focuses its input when the
   * root is clicked — grows it across the header; the title shrinks away to make room.
   *
   * Fluent's Combobox root is an `inline-grid` of `1fr auto` (input, then icon), so
   * collapsing means zeroing the input track rather than resizing the input: a `1fr` track
   * would otherwise keep handing the input whatever space the icon does not use. The
   * overrides are keyed off `comboboxClassNames`, which Fluent exports for exactly this,
   * and win on specificity as descendant rules.
   *
   * No JavaScript state is involved, so there is nothing to resynchronise when the viewport
   * changes.
   */
  search: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: SEARCH_WIDTH,
    minWidth: 0,
    maxWidth: SEARCH_WIDTH,
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: ICON_BUTTON_SIZE,
      width: ICON_BUTTON_SIZE,
      maxWidth: 'none',
      // Deliberately not transitioned. The Combobox root is the anchor for Fluent's
      // listbox popover, which Floating UI keeps aligned with a ResizeObserver. Animating
      // the anchor's width drives that observer once per frame, and each reposition
      // resizes the floating element in turn — a feedback loop that surfaces as
      // "ResizeObserver loop completed with undelivered notifications". The expand is
      // instant instead.

      // --- collapsed: geometry identical to an icon button ---
      [`& .${comboboxClassNames.root}`]: {
        gridTemplateColumns: '0 auto',
        justifyContent: 'center',
        paddingRight: 0,
        backgroundColor: 'transparent',
        borderTopStyle: 'none',
        borderRightStyle: 'none',
        borderBottomStyle: 'none',
        borderLeftStyle: 'none',
        // Clips the zero-width input. Both axes must be set: when only one axis is
        // non-`visible` the other computes to `auto` and adds a stray scrollbar.
        overflowX: 'hidden',
        overflowY: 'hidden',
        '::after': { display: 'none' },
      },
      [`& .${comboboxClassNames.input}`]: {
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0,
      },
      [`& .${comboboxClassNames.expandIcon}`]: {
        color: tokens.colorNeutralForegroundOnBrand,
      },

      // Collapsed hover matches the icon buttons. `:not(:focus-within)` keeps it from
      // fighting the expanded rules below, which have the same specificity.
      [`&:hover:not(:focus-within) .${comboboxClassNames.root}`]: {
        backgroundColor: tokens.colorNeutralBackground1,
      },
      [`&:hover:not(:focus-within) .${comboboxClassNames.expandIcon}`]: {
        color: HEADER_BACKGROUND,
      },

      // --- expanded: takes over the row and becomes a normal Fluent field again ---
      ':focus-within': {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '100%',
        width: 'auto',
      },
      [`&:focus-within .${comboboxClassNames.root}`]: {
        gridTemplateColumns: '1fr auto',
        justifyContent: 'space-between',
        paddingRight: tokens.spacingHorizontalMNudge,
        backgroundColor: tokens.colorNeutralBackground1,
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        // Restored so the focus underline, which sits at `bottom: -1px`, is not clipped.
        overflowX: 'visible',
        overflowY: 'visible',
        '::after': { display: 'block' },
      },
      [`&:focus-within .${comboboxClassNames.input}`]: {
        paddingLeft: `calc(${tokens.spacingHorizontalMNudge} + ${tokens.spacingHorizontalXXS})`,
      },
      [`&:focus-within .${comboboxClassNames.expandIcon}`]: {
        color: tokens.colorNeutralStrokeAccessible,
      },
    },
  },
  combobox: {
    width: '100%',
    // Fluent's Combobox defaults to a 250px min-width, which would stop it collapsing.
    minWidth: 'unset',
    maxWidth: '100%',
  },
  comboboxInput: {
    minWidth: 0,
    width: '100%',
  },
  listbox: {
    maxHeight: '60vh',
  },
  listboxMessage: {
    display: 'block',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
  optionDescription: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    columnGap: ICON_GAP,
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    minWidth: 'max-content',
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      // No growing on the compact layout: the buttons sit at their natural width so the
      // gap between the search icon and the first button is exactly `ICON_GAP`.
      flexGrow: 0,
      flexBasis: 'auto',
    },
  },
  /**
   * Hover and pressed invert the button: a solid light fill with the icon painted in the
   * bar's own colour. A translucent white wash was too weak against the lighter production
   * blue — inverting gives 5.4:1 there and 5.5:1 on the non-production red, well clear of
   * the 3:1 that WCAG 1.4.11 asks of non-text contrast.
   */
  actionButton: {
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      color: HEADER_BACKGROUND,
    },
    ':hover:active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      color: HEADER_BACKGROUND,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorNeutralForegroundOnBrand,
      outlineOffset: '-2px',
    },
    // A white focus ring would disappear against the inverted fill, so it flips too.
    ':hover:focus-visible': {
      outlineColor: HEADER_BACKGROUND,
    },
  },
  actionButtonChecked: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: HEADER_BACKGROUND,
  },
  badgeAnchor: {
    position: 'relative',
    display: 'inline-flex',
  },
  badge: {
    position: 'absolute',
    top: '-4px',
    insetInlineEnd: '-8px',
    pointerEvents: 'none',
  },
  avatarButton: {
    ...shorthands.padding(0),
    minWidth: '32px',
    ...shorthands.borderRadius('50%'),
  },
  avatarWrapper: {
    ...shorthands.borderRadius('50%'),
    overflowX: 'hidden',
    overflowY: 'hidden',
    lineHeight: 0,
  },

  drawer: {
    [HEADER_SMALL_BREAKPOINT]: {
      width: '100vw',
      maxWidth: '100vw',
    },
  },
});

export default useStatHeaderStyles;
